import {
  Button,
  Card,
  Dropdown,
  EmptyState,
  Flex,
  SimpleTable,
  Typography,
} from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const SimpleTablePage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>SimpleTable</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The{" "}
            <Text size="sm" strong>
              SimpleTable
            </Text>{" "}
            component is a flexible and responsive table layout for React
            applications. It provides a minimalistic design with support for
            custom content, loading states, and additional styling options. It
            also includes several subcomponents (
            <Text size="sm" strong>
              Head
            </Text>
            ,{" "}
            <Text size="sm" strong>
              TH
            </Text>
            ,{" "}
            <Text size="sm" strong>
              Body
            </Text>
            ,{" "}
            <Text size="sm" strong>
              Row
            </Text>
            ,{" "}
            <Text size="sm" strong>
              TD
            </Text>
            ) that allow for fine-tuned control of the table's structure and
            behavior.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Loading State
                </Text>
                : The table includes a built-in loading overlay, which can be
                activated via the{" "}
                <Text size="sm" strong>
                  loading
                </Text>{" "}
                prop. This is useful when fetching or processing data
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Row Interaction
                </Text>
                : The{" "}
                <Text size="sm" strong>
                  SimpleTableRow
                </Text>{" "}
                component allows you to define interactive table rows with
                optional hover and selection styles.
              </Text>
              <ul className="marker:text-sky-400 list-disc pl-6">
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Hover effect
                    </Text>
                    : Can be disabled by setting{" "}
                    <Text size="sm" strong>
                      withoutHoverStyle
                    </Text>{" "}
                    to true
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Selection
                    </Text>
                    : Rows can be marked as{" "}
                    <Text size="sm" strong>
                      selected
                    </Text>{" "}
                    for highlighting.
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Disabled
                    </Text>
                    : Rows can be marked as{" "}
                    <Text size="sm" strong>
                      disabled
                    </Text>
                    , which disables interaction.
                  </Text>
                </li>
              </ul>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Custom Table Structure
                </Text>
                : The{" "}
                <Text size="sm" strong>
                  SimpleTable
                </Text>{" "}
                component comes with several subcomponents to help define the
                structure of your table:
              </Text>
              <ul className="marker:text-sky-400 list-disc pl-6">
                <li>
                  <Text size="sm">
                    <Text size="xs" strong code>
                      SimpleTable.Head
                    </Text>
                    : Container for table headers
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="xs" strong code>
                      SimpleTable.TH
                    </Text>
                    : Customizable header cells with optional label support.
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="xs" strong code>
                      SimpleTable.Body
                    </Text>
                    : Container for table rows and data.
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="xs" strong code>
                      SimpleTable.Row
                    </Text>
                    : Configurable row component with custom behavior.
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="xs" strong code>
                      SimpleTable.TD
                    </Text>
                    : Customizable data cells.
                  </Text>
                </li>
              </ul>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>

          <ExpandableCard
            title="Simple Table"
            codeViewerProps={{
              code: SimpleTableExample1,
            }}
          >
            <Text strong size="sm">
              Simple table with data:
            </Text>
            <Flex className="gap-3 w-full">
              <SimpleTable className="gap-3 w-full">
                <SimpleTable.Head>
                  <SimpleTable.Row>
                    <SimpleTable.TH>Header 1</SimpleTable.TH>
                    <SimpleTable.TH>Header 2</SimpleTable.TH>
                    <SimpleTable.TH>Header 3</SimpleTable.TH>
                  </SimpleTable.Row>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  <SimpleTable.Row>
                    <SimpleTable.TD>Data 1</SimpleTable.TD>
                    <SimpleTable.TD>Data 2</SimpleTable.TD>
                    <SimpleTable.TD>Data 3</SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Flex>
            <Text strong size="sm" className="mt-10">
              Simple table with data and dropdown:
            </Text>
            <Flex className="gap-3 w-full">
              <SimpleTable className="gap-3 w-full">
                <SimpleTable.Head>
                  <SimpleTable.Row>
                    <SimpleTable.TH>Header 1</SimpleTable.TH>
                    <SimpleTable.TH>Header 2</SimpleTable.TH>
                    <SimpleTable.TH>Header 3</SimpleTable.TH>
                    <SimpleTable.TH></SimpleTable.TH>
                  </SimpleTable.Row>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  <SimpleTable.Row>
                    <SimpleTable.TD>Data 1</SimpleTable.TD>
                    <SimpleTable.TD>Data 2</SimpleTable.TD>
                    <SimpleTable.TD>Data 3</SimpleTable.TD>
                    <SimpleTable.TD>
                      <div className="h-full w-full inline-flex items-center justify-end gap-1 px-4">
                        <div className=" flex justify-center items-center">
                          <Dropdown
                            title="djuno Design"
                            menu={[
                              {
                                key: "1",
                                label: "Edit",
                              },
                              {
                                type: "divider",
                              },
                              {
                                key: "end",
                                label: "Delete",
                                danger: true,
                              },
                            ]}
                            buttonClassName="cursor-pointer"
                            anchor="bottom end"
                          >
                            <div className=" p-2 rounded-md text-dark-900 bg-secondary-100  dark:text-secondary-100 dark:bg-dark-900 dark:hover:bg-dark-950 ">
                              ...
                            </div>
                          </Dropdown>
                        </div>
                      </div>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Flex>
            <Text strong size="sm" className="mt-10">
              Simple table without data:
            </Text>
            <Flex className="w-full">
              <SimpleTable className="gap-3 w-full">
                <SimpleTable.Head>
                  <SimpleTable.Row>
                    <SimpleTable.TH>Header 1</SimpleTable.TH>
                    <SimpleTable.TH>Header 2</SimpleTable.TH>
                    <SimpleTable.TH>Header 3</SimpleTable.TH>
                  </SimpleTable.Row>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  <SimpleTable.Row withoutHoverStyle>
                    <SimpleTable.TD colSpan={3} className="text-center py-10">
                      <Flex className="w-full justify-center">
                        <EmptyState
                          text="No data available"
                          icon={<EmptyState.PRESENTED_IMAGE_SIMPLE />}
                        />
                      </Flex>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Flex>
          </ExpandableCard>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <Card id="SimpleTable API" description="SimpleTable">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        If true, a loading overlay will be displayed on top of
                        the table.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`boolean`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loadingSetting */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingSetting</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Custom loading setting</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L26"}
                        size="xs"
                        code
                      >
                        LoadingProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* className */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">className</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        {`Custom class name applied to the <table> element.`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* containerClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">containerClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        {`Custom class name applied to the table container <div>.`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content of the table (usually includes table head,
                        body, rows, etc.).
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
            <Card id="SimpleTable.Head API" description="SimpleTable.Head">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Content for the table head, usually SimpleTableTH
                        components.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
            <Card id="SimpleTable.TH API" description="SimpleTable.TH">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* label */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">label</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Label for the table header. You can also pass children
                        as the content instead of label.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string | React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Header content, will override the label if provided.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
            <Card id="SimpleTable.Body API" description="SimpleTable.Body">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Content for the table body, usually SimpleTableRow
                        components.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
            <Card id="SimpleTable.Row API" description="SimpleTable.Row">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* selected */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">selected</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Marks the row as selected with different styles.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`boolean`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* withoutHoverStyle */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">withoutHoverStyle</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Disables the hover style if true.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`boolean`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* className */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">className</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`Custom class name for the row.`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* disabled */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">disabled</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`Disables the row interaction.`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`boolean`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* onClick */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onClick</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`Function to handle row click event.`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`(e: React.MouseEvent<HTMLTableRowElement>) => void`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content of the table (usually includes table head,
                        body, rows, etc.).
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
            <Card id="SimpleTable.TD API" description="SimpleTable.TD">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* className */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">className</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`Custom class name for the row.`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content of the table (usually includes table head,
                        body, rows, etc.).
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* colspan */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">colspan</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Attribute in HTML tables allows a cell td or th to span
                        across multiple columns, merging them into one.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const SimpleTableExample1 = `<Text strong size="sm">
  Simple table with data:
</Text>
<Flex className="gap-3 w-full">
  <SimpleTable className="gap-3 w-full">
    <SimpleTable.Head>
      <SimpleTable.Row>
        <SimpleTable.TH>Header 1</SimpleTable.TH>
        <SimpleTable.TH>Header 2</SimpleTable.TH>
        <SimpleTable.TH>Header 3</SimpleTable.TH>
      </SimpleTable.Row>
    </SimpleTable.Head>
    <SimpleTable.Body>
      <SimpleTable.Row>
        <SimpleTable.TD>Data 1</SimpleTable.TD>
        <SimpleTable.TD>Data 2</SimpleTable.TD>
        <SimpleTable.TD>Data 3</SimpleTable.TD>
      </SimpleTable.Row>
    </SimpleTable.Body>
  </SimpleTable>
</Flex>
<Text strong size="sm" className="mt-10">
  Simple table with data and dropdown:
</Text>
<Flex className="gap-3 w-full">
  <SimpleTable className="gap-3 w-full">
    <SimpleTable.Head>
      <SimpleTable.Row>
        <SimpleTable.TH>Header 1</SimpleTable.TH>
        <SimpleTable.TH>Header 2</SimpleTable.TH>
        <SimpleTable.TH>Header 3</SimpleTable.TH>
        <SimpleTable.TH></SimpleTable.TH>
      </SimpleTable.Row>
    </SimpleTable.Head>
    <SimpleTable.Body>
      <SimpleTable.Row>
        <SimpleTable.TD>Data 1</SimpleTable.TD>
        <SimpleTable.TD>Data 2</SimpleTable.TD>
        <SimpleTable.TD>Data 3</SimpleTable.TD>
        <SimpleTable.TD>
          <div className="h-full w-full inline-flex items-center justify-end gap-1 px-4">
            <div className=" flex justify-center items-center">
              <Dropdown
                title="djuno Design"
                menu={[
                  {
                    key: "1",
                    label: "Edit",
                  },
                  {
                    type: "divider",
                  },
                  {
                    key: "end",
                    label: "Delete",
                    danger: true,
                  },
                ]}
                type="simple"
              >
                <div className=" p-2 rounded-md text-dark-900 bg-secondary-100  dark:text-secondary-100 dark:bg-dark-900 dark:hover:bg-dark-950 ">
                  Djuno Design
                </div>
              </Dropdown>
            </div>
          </div>
        </SimpleTable.TD>
      </SimpleTable.Row>
    </SimpleTable.Body>
  </SimpleTable>
</Flex>
<Text strong size="sm" className="mt-10">
  Simple table without data:
</Text>
<Flex className="w-full">
  <SimpleTable className="gap-3 w-full">
    <SimpleTable.Head>
      <SimpleTable.Row>
        <SimpleTable.TH>Header 1</SimpleTable.TH>
        <SimpleTable.TH>Header 2</SimpleTable.TH>
        <SimpleTable.TH>Header 3</SimpleTable.TH>
      </SimpleTable.Row>
    </SimpleTable.Head>
    <SimpleTable.Body>
      <SimpleTable.Row withoutHoverStyle>
        <SimpleTable.TD colSpan={3} className="text-center py-10">
          <Flex className="w-full justify-center">
            <EmptyState
              text="No data available"
              icon={<EmptyState.PRESENTED_IMAGE_SIMPLE />}
            />
          </Flex>
        </SimpleTable.TD>
      </SimpleTable.Row>
    </SimpleTable.Body>
  </SimpleTable>
</Flex>`;

export default SimpleTablePage;
